/**
 * Phase 8 chunk 5A — NotifyService.
 *
 * Pure service module that brokers the Notifications API on behalf of
 * the rest of the UI. Cesium-free + DOM-light so it can be Vitest-tested
 * in the node environment (matchMedia / Notification / localStorage are
 * all probed defensively).
 *
 * Responsibilities:
 *   - Permission state inspection + request flow
 *   - Per-user preferences (lead time minutes, analytics opt-out) via
 *     localStorage, with a strict allowlist for lead-time minutes
 *   - Schedule-entry builder: computes the actual fire time from a
 *     pass's `rise_at` + the current lead-time preference
 *   - iOS PWA detection (web push requires Add to Home Screen on iOS)
 *   - Test-notification trigger so users can verify the path end-to-end
 *
 * The actual notification *firing* (in-page setTimeout for the
 * tab-open case + service-worker timer for the tab-closed case) lives
 * in chunks 5C + 5D respectively; this module is the common substrate.
 */

export type NotificationPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

export const NOTIFY_LEAD_MINUTES_KEY = 'sat:notificationLeadMinutes';
export const ANALYTICS_OPT_OUT_KEY = 'sat:disableAnalytics';
export const ALLOWED_LEAD_MINUTES = [2, 5, 10, 15] as const;
export const DEFAULT_LEAD_MINUTES = 5;

export type AllowedLeadMinutes = (typeof ALLOWED_LEAD_MINUTES)[number];

/** A pre-armed notification entry. */
export interface NotifyScheduleEntry {
  /** NORAD id of the satellite this pass belongs to. */
  norad: number;
  /** Human-friendly satellite name for the body text. */
  satelliteName: string;
  /** ISO-8601 rise time of the pass (UTC). */
  riseAt: string;
  /** Peak elevation in degrees, used in the body text. */
  maxElevationDeg: number;
  /** Compass direction of peak (e.g. "SW"), used in the body text. */
  peakCompass: string;
  /** Optional visual magnitude — drives the "naked-eye" callout. */
  magnitude: number | null;
  /** When the notification should fire (ms since epoch). */
  fireAtMs: number;
  /** Optional observer hint used to construct the deep-link target. */
  observer: { latitude: number; longitude: number } | null;
}

/**
 * Returns the live permission state, including a distinct
 * 'unsupported' value when the API is unavailable (Vitest node env,
 * old browsers, http contexts on some platforms).
 */
export function currentPermissionState(): NotificationPermissionState {
  if (typeof window === 'undefined') return 'unsupported';
  if (typeof Notification === 'undefined') return 'unsupported';
  const p = Notification.permission;
  if (p === 'granted' || p === 'denied' || p === 'default') return p;
  return 'unsupported';
}

/**
 * Wraps `Notification.requestPermission()` with sane defaults and a
 * graceful path when the API is missing. Returns the resulting state.
 */
export async function requestPermission(): Promise<NotificationPermissionState> {
  if (typeof Notification === 'undefined') return 'unsupported';
  try {
    const result = await Notification.requestPermission();
    if (result === 'granted' || result === 'denied' || result === 'default') return result;
    return 'unsupported';
  } catch {
    return 'unsupported';
  }
}

export function readLeadMinutes(): AllowedLeadMinutes {
  if (typeof localStorage === 'undefined') return DEFAULT_LEAD_MINUTES;
  try {
    const raw = localStorage.getItem(NOTIFY_LEAD_MINUTES_KEY);
    if (raw === null) return DEFAULT_LEAD_MINUTES;
    const parsed = parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return DEFAULT_LEAD_MINUTES;
    if ((ALLOWED_LEAD_MINUTES as readonly number[]).includes(parsed)) {
      return parsed as AllowedLeadMinutes;
    }
    return DEFAULT_LEAD_MINUTES;
  } catch {
    return DEFAULT_LEAD_MINUTES;
  }
}

export function writeLeadMinutes(value: AllowedLeadMinutes): void {
  if (typeof localStorage === 'undefined') return;
  if (!(ALLOWED_LEAD_MINUTES as readonly number[]).includes(value)) return;
  try {
    localStorage.setItem(NOTIFY_LEAD_MINUTES_KEY, String(value));
  } catch {
    // private-mode / quota exceeded — silent no-op
  }
}

export function readAnalyticsOptOut(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(ANALYTICS_OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeAnalyticsOptOut(value: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (value) localStorage.setItem(ANALYTICS_OPT_OUT_KEY, '1');
    else localStorage.removeItem(ANALYTICS_OPT_OUT_KEY);
  } catch {
    // ignore
  }
}

/**
 * Clears every persisted user preference this service owns. Caller
 * (<sat-settings>) wires this to the "Forget my preferences" button.
 */
export function forgetPreferences(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(NOTIFY_LEAD_MINUTES_KEY);
    localStorage.removeItem(ANALYTICS_OPT_OUT_KEY);
  } catch {
    // ignore
  }
}

/** Returns true if the page is running as a standalone PWA (iOS / Android). */
export function isStandaloneDisplayMode(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(display-mode: standalone)').matches === true;
  } catch {
    return false;
  }
}

/** Returns true if the user agent looks like iOS Safari. */
export function isLikelyIos(userAgent: string = typeof navigator !== 'undefined' ? navigator.userAgent : ''): boolean {
  if (!userAgent) return false;
  return /iPhone|iPad|iPod/.test(userAgent) && !/CriOS|FxiOS/.test(userAgent);
}

/**
 * iOS prior to 16.4 cannot show web-push notifications at all.
 * iOS 16.4+ requires the page be installed as a PWA (display-mode:
 * standalone). When this returns true the caller should show an
 * "Add to Home Screen first" tooltip instead of opening the permission
 * prompt — Safari will silently deny if it isn't standalone.
 */
export function iosNeedsHomeScreenInstall(): boolean {
  return isLikelyIos() && !isStandaloneDisplayMode();
}

/**
 * Computes the actual fire-time given a pass's rise time and the
 * caller's current lead-minutes preference.
 *
 * Defensive against non-ISO strings (returns NaN, which the caller can
 * filter on) so a stale row from the API doesn't poison scheduling.
 */
export function computeFireAtMs(riseAtIso: string, leadMinutes: number): number {
  const rise = Date.parse(riseAtIso);
  if (!Number.isFinite(rise)) return Number.NaN;
  if (!Number.isFinite(leadMinutes)) return Number.NaN;
  return rise - leadMinutes * 60_000;
}

/**
 * Builds a schedule entry from a pass record. Returns `null` if the
 * fire-time is already in the past (the pass has begun or there isn't
 * enough lead time left).
 */
export function buildScheduleEntry(input: {
  norad: number;
  satelliteName: string;
  riseAtIso: string;
  maxElevationDeg: number;
  peakCompass: string;
  magnitude: number | null;
  leadMinutes: number;
  observer: { latitude: number; longitude: number } | null;
  now?: number;
}): NotifyScheduleEntry | null {
  const fireAtMs = computeFireAtMs(input.riseAtIso, input.leadMinutes);
  if (!Number.isFinite(fireAtMs)) return null;
  const now = input.now ?? Date.now();
  if (fireAtMs <= now) return null;
  return {
    norad: input.norad,
    satelliteName: input.satelliteName,
    riseAt: input.riseAtIso,
    maxElevationDeg: input.maxElevationDeg,
    peakCompass: input.peakCompass,
    magnitude: input.magnitude,
    fireAtMs,
    observer: input.observer,
  };
}

/**
 * Constructs the deep-link the notification's click handler should
 * open. Honors the user's last-known observer position so they can
 * jump straight into sky view if Phase 7 is available.
 */
export function buildNotifyDeepLink(entry: NotifyScheduleEntry): string {
  const params = new URLSearchParams();
  params.set('sat', String(entry.norad));
  if (entry.observer !== null) {
    params.set('view', 'sky');
    params.set('lat', entry.observer.latitude.toFixed(4));
    params.set('lon', entry.observer.longitude.toFixed(4));
  }
  return `/?${params.toString()}`;
}

/**
 * Builds the body text used both by the test notification and by the
 * real per-pass notification. Reusing this keeps both paths identical
 * so the "Test notification" affordance accurately previews the real
 * one.
 */
export function buildNotifyBody(entry: NotifyScheduleEntry, leadMinutes: number): string {
  const rise = new Date(entry.riseAt);
  const hh = String(rise.getUTCHours()).padStart(2, '0');
  const mm = String(rise.getUTCMinutes()).padStart(2, '0');
  const magNote = entry.magnitude !== null && entry.magnitude <= 4.5
    ? ' · naked-eye visible'
    : '';
  return `Rises ${hh}:${mm} UTC · max el ${entry.maxElevationDeg.toFixed(0)}° ${entry.peakCompass}${magNote} (in ${leadMinutes} min)`;
}

/**
 * Fires an immediate sample notification so users can verify
 * permissions + see what a real per-pass notification will look like.
 * Returns the resulting state or 'unsupported' / 'denied' on failure.
 */
export async function showTestNotification(): Promise<NotificationPermissionState> {
  const state = currentPermissionState();
  if (state !== 'granted') return state;
  if (typeof Notification === 'undefined') return 'unsupported';
  try {
    const n = new Notification('sat.trackr.live test notification', {
      body: 'If you see this, real pass notifications will work too. Adjust the lead time in §Settings.',
      icon: '/icons/icon-192.png',
      tag: 'sat-trackr:test',
    });
    setTimeout(() => n.close(), 8_000);
    return 'granted';
  } catch {
    return 'unsupported';
  }
}
